import { safeStorage } from "../helpers.js";

/**
 * ArchiveCollapse component
 * Manages collapsing/expanding past week sections on year pages
 * Persists user preference in localStorage (with cookie fallback)
 */

const STORAGE_KEY = "year-archive-always-open";

/**
 * @param {{expandButton: HTMLButtonElement, toggleCheckbox: HTMLInputElement}} param0
 * @returns {{show: () => void, hide: () => void, isHidden: () => boolean}}
 */
const ArchiveCollapse = ({ expandButton, toggleCheckbox }) => {
  /** @type {NodeListOf<HTMLElement>} */
  const pastWeekSections = document.querySelectorAll(".past-week");

  if (!expandButton || !toggleCheckbox || pastWeekSections.length === 0) {
    return {
      show: () => {},
      hide: () => {},
      isHidden: () => false,
    };
  }

  /**
   * Check if archive should start hidden based on user preference
   * @returns {boolean}
   */
  const shouldStartHidden = () => {
    const preference = safeStorage.getItem(STORAGE_KEY);
    return preference !== "true"; // Hidden by default, unless user opted to keep open
  };

  /**
   * Show all past week sections
   */
  const show = () => {
    pastWeekSections.forEach((section) => {
      section.classList.remove("hidden");
    });
    expandButton.style.display = "none";
  };

  /**
   * Hide all past week sections
   */
  const hide = () => {
    pastWeekSections.forEach((section) => {
      section.classList.add("hidden");
    });
    expandButton.style.display = "flex";
  };

  /**
   * Check if archive is currently hidden
   * @returns {boolean}
   */
  const isHidden = () => {
    return pastWeekSections[0]?.classList.contains("hidden") ?? false;
  };

  /**
   * Save preference to storage
   * @param {boolean} alwaysOpen
   */
  const savePreference = (alwaysOpen) => {
    safeStorage.setItem(STORAGE_KEY, alwaysOpen ? "true" : "false");
  };

  // Event listeners
  expandButton.addEventListener("click", () => {
    show();
  });

  toggleCheckbox.addEventListener("change", () => {
    const alwaysOpen = toggleCheckbox.checked;
    savePreference(alwaysOpen);

    if (alwaysOpen) {
      show();
    } else {
      hide();
    }
  });

  // Initialize state based on preference
  const alwaysOpen = !shouldStartHidden();
  toggleCheckbox.checked = alwaysOpen;

  if (alwaysOpen) {
    show();
  } else {
    hide();
  }

  return {
    show,
    hide,
    isHidden,
  };
};

export default ArchiveCollapse;
